/**
 * AppShell Navigation Config — single source of truth.
 *
 * Drives: sidebar menu groups, breadcrumbs, route registration, and the
 * DevToolbar route switcher. Mirrors docs/MODULES_MAP.md and the legacy
 * `modules` / `module_menus` tables from build #4.
 *
 * An item may define `children` to render an expandable sidebar submenu
 * (e.g. Product → List Produk / List Product Type / List Kategori). Parent
 * items are containers only: their `path` is used for the sidebar row, while
 * routing (MODULE_ITEMS) is driven by the leaf children.
 */
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBasket,
  Undo2,
  Package,
  Truck,
  Users,
  Wallet,
  BarChart3,
  TrendingUp,
  BookOpen,
  Megaphone,
  Star,
  Settings,
  List,
  Tag,
  FolderOpen,
  ShoppingBag,
  ReceiptText,
  HandCoins,
  Banknote,
  SlidersHorizontal,
  UserCog,
  Ruler,
  FolderCog,
  Boxes,
  Sparkles,
  PlugZap,
  Smartphone,
  MessageSquare,
  Activity,
  FileText,
} from 'lucide-react';

export const MODULE_GROUPS = [
  {
    id: 'dashboard',
    label: null,
    items: [
      {
        id: 'dashboard',
        path: '/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        description: 'Executive Dashboard',
      },
    ],
  },
  {
    id: 'data-crm',
    label: 'DATA & CRM',
    items: [
      {
        id: 'suppliers',
        path: '/suppliers',
        label: 'Supplier',
        icon: Truck,
        description: 'Supplier Directory',
      },
      {
        id: 'customers',
        path: '/customers',
        label: 'Customer',
        icon: Users,
        description: 'CRM Suite & Customers',
      },
      {
        id: 'products',
        path: '/products',
        label: 'Produk',
        icon: Package,
        description: 'Inventory & SKU Manager',
        children: [
          {
            id: 'product-list',
            path: '/products',
            label: 'List Produk',
            icon: List,
            description: 'Product List',
          },
          {
            id: 'product-types',
            path: '/product-types',
            label: 'List Product Type',
            icon: Tag,
            description: 'Product Types',
          },
          {
            id: 'product-categories',
            path: '/product-categories',
            label: 'List Kategori',
            icon: FolderOpen,
            description: 'Product Categories',
          },
          {
            id: 'product-units',
            path: '/product-units',
            label: 'Satuan Produk',
            icon: Ruler,
            description: 'Product Units',
          },
        ],
      },
    ],
  },
  {
    id: 'operasi',
    label: 'OPERASI',
    items: [
      {
        id: 'purchases',
        path: '/purchases',
        label: 'Pembelian',
        icon: ShoppingBasket,
        description: 'Purchasing & Intake',
      },
      {
        id: 'sales',
        path: '/sales',
        label: 'Penjualan',
        icon: ShoppingCart,
        description: 'Sales & POS Terminal',
        children: [
          {
            id: 'sales-pos',
            path: '/sales',
            label: 'Penjualan (POS)',
            icon: ShoppingBag,
            description: 'Sales & POS Terminal',
          },
          {
            id: 'returns',
            path: '/returns',
            label: 'Retur',
            icon: Undo2,
            description: 'Sales & Purchase Returns',
          },
        ],
      },
    ],
  },
  {
    id: 'keuangan',
    label: 'KEUANGAN',
    items: [
      {
        id: 'finance',
        path: '/expenses',
        label: 'Keuangan',
        icon: Wallet,
        description: 'Finance, Debt & Credit',
        children: [
          {
            id: 'expenses',
            path: '/expenses',
            label: 'Expense',
            icon: ReceiptText,
            description: 'Expense List',
          },
          {
            id: 'debts',
            path: '/debts',
            label: 'Debt',
            icon: HandCoins,
            description: 'Debt List',
          },
          {
            id: 'credits',
            path: '/credits',
            label: 'Credit',
            icon: Banknote,
            description: 'Credit List',
          },
          {
            id: 'expense-categories',
            path: '/expense-categories',
            label: 'Kategori Pengeluaran',
            icon: FolderCog,
            description: 'Expense Categories',
          },
        ],
      },
    ],
  },
  {
    id: 'laporan',
    label: 'LAPORAN',
    items: [
      {
        id: 'reports',
        path: '/reports',
        label: 'Laporan',
        icon: BarChart3,
        description: 'Reports & Analytics',
      },
    ],
  },
  {
    id: 'konten',
    label: 'KONTEN',
    items: [
      {
        id: 'catalogue',
        path: '/catalogue',
        label: 'Katalog Digital',
        icon: BookOpen,
        description: 'Digital Catalogue',
        children: [
          {
            id: 'catalogue-list',
            path: '/catalogue',
            label: 'List Katalog',
            icon: BookOpen,
            description: 'Digital Catalogue',
          },
          {
            id: 'catalogue-products',
            path: '/catalogue/products',
            label: 'Semua Produk',
            icon: Package,
            description: 'All Catalogue Products',
          },
          {
            id: 'highlights',
            path: '/highlights',
            label: 'Highlight',
            icon: Star,
            description: 'Highlight Manager',
          },
        ],
      },
      {
        id: 'catalogue-new',
        path: '/catalogue-new',
        label: 'Catalogue (New)',
        icon: Boxes,
        description: 'Catalogue Batch Intake',
        children: [
          {
            id: 'catalogue-new-daftar-bc',
            path: '/catalogue-new',
            label: 'Daftar BC',
            icon: List,
            description: 'Daftar Batch Catalogue',
          },
          {
            id: 'catalogue-new-kurasi-product',
            path: '/catalogue-new/curation',
            label: 'Kurasi Product',
            icon: Sparkles,
            description: 'Kurasi Produk dari Batch Catalogue',
          },
          {
            id: 'catalogue-new-list-produk',
            path: '/catalogue-new/list-produk',
            label: 'List Produk',
            icon: Package,
            description: 'Produk Terpublish dari Kurasi',
          },
          {
            id: 'catalogue-new-katalog-pdf',
            path: '/catalogue-new/pdf',
            label: 'Katalog PDF',
            icon: FileText,
            description: 'Katalog PDF dari Produk Terpublish',
          },
          {
            id: 'promotions',
            path: '/promotions',
            label: 'Promosi',
            icon: Megaphone,
            description: 'Promotion Manager',
          },
        ],
      },
    ],
  },

  {
    id: 'analisis',
    label: 'ANALISIS',
    items: [
      {
        id: 'analytics',
        path: '/analytics',
        label: 'Analitik Pengunjung',
        icon: TrendingUp,
        description: 'Visitor Analytics',
      },
    ],
  },
  {
    id: 'broadcast-group',
    label: 'BROADCAST',
    items: [
      {
        id: 'broadcast',
        path: '/broadcast/contacts',
        label: 'Broadcast',
        icon: Megaphone,
        description: 'Pesan Massal & Broadcast',
        children: [
          {
            id: 'broadcast-contacts',
            path: '/broadcast/contacts',
            label: 'Kontak',
            icon: Users,
            description: 'Daftar Kontak Broadcast',
          },
          {
            id: 'broadcast-groups',
            path: '/broadcast/groups',
            label: 'WhatsApp Group',
            icon: MessageSquare,
            description: 'Daftar WhatsApp Group Admin',
          },
          {
            id: 'broadcast-activity',
            path: '/broadcast/activity',
            label: 'Aktivitas & Laporan',
            icon: Activity,
            description: 'Riwayat, Monitoring, dan Laporan Broadcast',
          },
        ],
      },
    ],
  },
  {
    id: 'sistem',
    label: 'SISTEM',
    items: [
      {
        id: 'settings',
        path: '/settings',
        label: 'Pengaturan',
        icon: Settings,
        description: 'Settings & Roles',
        children: [
          {
            id: 'settings-general',
            path: '/settings/general',
            label: 'Umum',
            icon: SlidersHorizontal,
            description: 'General Settings',
          },
          {
            id: 'settings-users',
            path: '/settings/users',
            label: 'Pengguna',
            icon: UserCog,
            description: 'User Management',
            requiresRole: 'superadmin',
          },
          {
            id: 'settings-marketing',
            path: '/settings/marketing',
            label: 'Pemasaran',
            icon: Megaphone,
            description: 'Link Channel & Komunitas WhatsApp di website',
          },
          {
            id: 'settings-whatsapp',
            path: '/settings/whatsapp',
            label: 'WhatsApp Gateway',
            icon: Smartphone,
            description: 'Koneksi & Pairing WhatsApp (Libericano)',
          },
          {
            id: 'settings-whatsapp-gateway',
            path: '/settings/whatsapp-gateway',
            label: 'Konfigurasi Gateway',
            icon: PlugZap,
            description: 'URL, API Key & Signing Secret Webhook Libericano',
            requiresRole: 'superadmin',
          },
          {
            id: 'settings-ai',
            path: '/settings/ai',
            label: 'AI Provider',
            icon: Sparkles,
            description: 'API Key, Model & Batas Layanan AI',
            requiresRole: 'superadmin',
          },
        ],
      },
    ],
  },
];

// Leaf-only flat list (parents are containers): used for route registration
// and the DevToolbar route switcher.
export const MODULE_ITEMS = MODULE_GROUPS.flatMap((group) =>
  group.items
    .flatMap((item) => (item.children?.length ? item.children : [item]))
    .map((item) => ({ ...item, groupId: group.id }))
);

export function isPathMatch(currentPath, targetPath) {
  if (!currentPath || !targetPath) return false;
  if (currentPath === targetPath) return true;
  const normalized = targetPath.endsWith('/') ? targetPath : `${targetPath}/`;
  return currentPath.startsWith(normalized);
}

export function findModuleByPath(pathname) {
  const exact = MODULE_ITEMS.find((item) => item.path === pathname);
  if (exact) return exact;
  return MODULE_ITEMS
    .filter((item) => isPathMatch(pathname, item.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export function findGroupByPath(pathname) {
  const item = findModuleByPath(pathname);
  if (!item) return null;
  return MODULE_GROUPS.find((group) => group.id === item.groupId);
}

/**
 * Standalone pages outside the sidebar (account-menu info pages).
 * Kept here so the breadcrumb knows a friendly label for them.
 */
export const STATIC_PAGES = {
  '/about': { label: 'Tentang' },
  '/privacy': { label: 'Privasi' },
  '/license': { label: 'Lisensi' },
  '/help': { label: 'Bantuan' },
  '/broadcast/campaigns': { label: 'Riwayat Broadcast' },
  '/broadcast/monitor': { label: 'Monitor Realtime' },
  '/broadcast/messages': { label: 'Riwayat Pesan' },
  '/broadcast/reports': { label: 'Statistik & Laporan' },
};

/**
 * Breadcrumb trail for the current path, e.g.
 *   /dashboard          → [{ label: 'Dashboard' }]
 *   /products           → [{ label: 'Data & CRM' }, { label: 'Produk & SKU' }]
 *   /about              → [{ label: 'Tentang' }]
 */
export function getBreadcrumbs(pathname) {
  const staticPage = STATIC_PAGES[pathname];
  if (staticPage) return [{ label: staticPage.label, path: pathname }];

  const item = findModuleByPath(pathname);
  if (!item) return [{ label: 'Dashboard', path: '/dashboard' }];

  const group = MODULE_GROUPS.find((g) => g.id === item.groupId);
  const crumbs = [];
  if (group?.label) {
    crumbs.push({ label: group.label, path: group.items[0]?.path });
  }
  crumbs.push({ label: item.label, path: item.path });
  return crumbs;
}
